import { Division, EmptyState, Flex, Heading, Icons, Paragraph } from "@ir/ui";
import type { BrandFeature } from "@src/domain/entities/brand";
import type { FeatureLookupItem } from "@src/domain/dto/features";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import { BrandFeatureStatusUpdate } from "./BrandFeatureStatusUpdate";

export interface BrandDetailsFeaturesContentProps {
  brandId: number;
  featuresLookup: FeatureLookupItem[] | null;
  brandFeatures: BrandFeature[] | null;
  onFeatureStatusUpdated?: (feature: BrandFeature, featureKey: string) => void;
}

function findBrandFeatureByKey(
  brandFeatures: BrandFeature[] | null,
  featureKey: string,
): BrandFeature | undefined {
  if (!brandFeatures?.length) return undefined;
  return brandFeatures.find((bf) => bf.brandFeatureKey === featureKey);
}

export const BrandDetailsFeaturesContent = ({
  brandId,
  featuresLookup,
  brandFeatures,
  onFeatureStatusUpdated,
}: BrandDetailsFeaturesContentProps) => (
  <ErrorBoundary>
    {featuresLookup?.length ? (
      featuresLookup.map((feature) => {
        const assigned = findBrandFeatureByKey(
          brandFeatures,
          feature.featureKey,
        );
        const isActive = Boolean(
          assigned?.brandFeatureIsActive &&
            assigned.brandFeatureMappingId != null,
        );
        const displayName =
          feature.featureDisplayName ?? feature.featureName ?? "";
        return (
          <Flex key={feature.featureId} align="center" className="py-2">
            <Division style={{ flex: 1 }}>
              <Paragraph
                className="text-grey-700 font-bold m-0"
                style={{ lineHeight: "1" }}
              >
                {displayName}
              </Paragraph>

              {feature.featurePermissionsCount != null && (
                <Paragraph
                  className="m-0 p-0 text-grey-500 font-regular text-sm"
                  style={{ fontSize: "0.65rem" }}
                >
                  {feature.featurePermissionsCount} Permission
                  {feature.featurePermissionsCount === 1 ? "" : "s"}
                </Paragraph>
              )}
            </Division>
            <Division>
              <BrandFeatureStatusUpdate
                brandId={brandId}
                featureId={feature.featureId}
                isActive={isActive}
                onUpdateSuccess={(updatedFeature) =>
                  onFeatureStatusUpdated?.(updatedFeature, feature.featureKey)
                }
              />
            </Division>
          </Flex>
        );
      })
    ) : (
      <EmptyState
        className="bg-white border-1 border-color-grey-25 px-4 py-5 m-4"
        icon={Icons.InfoOctagon}
        title={
          <Heading level={6} className="text-grey-700 font-bold mt-3">
            No feature available for this brand.
          </Heading>
        }
      />
    )}
  </ErrorBoundary>
);
